import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, Link2, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Messages posés par Nexus pour la personne (GET /nexus/notices) : en tête du chat, avec leur bouton d'action, jusqu'à
// que l'action soit faite (le serveur retire alors le message de lui-même). Ouvrir le chat les marque « vus » : le
// bouton de Nexus cesse de les signaler ; un message sans action s'en va quand on referme le chat (Rémy, 23/10 : pas de
// croix, ouvrir le chat suffit). Un message nouveau
// fait aussi une notification du navigateur, si la personne l'a permis (une fois par message, tous onglets confondus).

const POLL_MS = 60000; // aussi onglet en arrière-plan : c'est là que la notification du navigateur sert
const NOTIFIED_KEY = 'deepflow.nexus.notified';
const shownNotifications = new Map(); // id du message → notification affichée par cet onglet

const browserNotifications = () => (typeof window !== 'undefined' && 'Notification' in window ? window.Notification : null);

export const notificationPermission = () => browserNotifications()?.permission || 'unsupported';

const readNotified = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(NOTIFIED_KEY) || '[]'));
  } catch (err) {
    return new Set();
  }
};

/** Nouveaux messages à signaler : ceux qui n'ont encore jamais fait de notification. Les note comme signalés. */
export const toNotify = (notices) => {
  const notified = readNotified();
  const fresh = notices.filter((n) => !notified.has(n.id));
  if (fresh.length) {
    try {
      localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...notified, ...fresh.map((n) => n.id)].slice(-200)));
    } catch (err) {
      // stockage indisponible : au pire, une notification en double
    }
  }
  return fresh;
};

const notify = (notice) => {
  const Notice = browserNotifications();
  if (!Notice || Notice.permission !== 'granted') return;
  try {
    const shown = new Notice(`Nexus · ${notice.title}`, {
      body: notice.body.length > 180 ? `${notice.body.slice(0, 177)}…` : notice.body,
      icon: '/favicon.ico', tag: `nexus-notice-${notice.id}`,
    });
    shownNotifications.set(notice.id, shown);
    shown.onclick = () => {
      window.focus();
      window.dispatchEvent(new CustomEvent('nexus-open', { detail: {} }));
      shown.close();
    };
  } catch (err) {
    // navigateur qui refuse (ex. téléphone sans service worker) : le chat suffit
  }
};

export const useNexusNotices = () => {
  const [notices, setNotices] = useState([]);
  const load = useCallback(() => apiService.get('/nexus/notices').then((rows) => {
    const list = Array.isArray(rows) ? rows : [];
    setNotices(list);
    // Sans permission, rien n'est noté comme signalé : la notification viendra une fois la permission donnée.
    // Déjà vu dans le chat (ici ou ailleurs) : plus de notification.
    if (notificationPermission() === 'granted') toNotify(list.filter((n) => !n.seen_at)).forEach(notify);
  }).catch(() => {}), []);
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    // Une page qui vient de montrer ce qu'un message annonçait (ex. messages du portail client) le fait relire aussitôt.
    window.addEventListener('nexus-notices-refresh', load);
    return () => { clearInterval(timer); window.removeEventListener('nexus-notices-refresh', load); };
  }, [load]);
  const dismiss = useCallback((notice) => {
    setNotices((all) => all.filter((n) => n.id !== notice.id));
    apiService.post(`/nexus/notices/${notice.id}/read`, {}).catch(() => {});
  }, []);
  const markSeen = useCallback(() => {
    const ids = notices.filter((n) => !n.seen_at).map((n) => n.id);
    if (!ids.length) return;
    const at = new Date().toISOString();
    setNotices((all) => all.map((n) => (ids.includes(n.id) ? { ...n, seen_at: at } : n)));
    ids.forEach((id) => { shownNotifications.get(id)?.close(); shownNotifications.delete(id); });
    apiService.post('/nexus/notices/seen', { ids }).catch(() => {});
  }, [notices]);
  return { notices, unseen: notices.filter((n) => !n.seen_at), dismiss, markSeen };
};

// onDismiss : la croix retire la carte (lue) ; sinon une carte avec un lien restait au bas du chat.
export const NexusNoticeCard = ({ notice, onDismiss }) => {
  const [permission, setPermission] = useState(notificationPermission);
  const ask = async () => {
    try {
      setPermission(await browserNotifications().requestPermission());
    } catch (err) {
      setPermission('denied');
    }
  };
  return (
    <div className="flex-1 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-white via-white to-indigo-50/70 p-4 shadow">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Nexus</p>
        {onDismiss && (
          <button type="button" onClick={() => onDismiss(notice)} aria-label="Masquer ce message" title="Masquer ce message"
            className="-mr-1 -mt-1 rounded-full p-1 text-slate-400 hover:bg-indigo-50 hover:text-slate-700"><X size={14} /></button>
        )}
      </div>
      <h4 className="mt-1 text-sm font-bold text-slate-900">{notice.title}</h4>
      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{notice.body}</p>
      {notice.action_url && (
        <a href={notice.action_url}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700">
          <Link2 size={14} /> {notice.action_label || 'Ouvrir'}
        </a>
      )}
      {permission === 'default' && (
        <button type="button" onClick={ask}
          className="mt-2 flex w-full items-center justify-center gap-1.5 text-xs font-medium text-indigo-600 hover:underline">
          <BellRing size={12} /> Me prévenir aussi par une notification du navigateur
        </button>
      )}
      {permission === 'denied' && (
        <p className="mt-2 text-center text-[11px] text-slate-400">Notifications du navigateur bloquées pour Deepflow (réglages du site dans le navigateur).</p>
      )}
      {permission === 'granted' && (
        <p className="mt-2 text-center text-[11px] text-slate-400">Notifications du navigateur activées pour les messages de Nexus.</p>
      )}
    </div>
  );
};
